import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  ArrowRight,
  BatteryCharging,
  ChevronDown,
  ChevronUp,
  ListChecks,
  MapPin,
  MessageSquare,
  Plug,
  Route,
  Layers,
  ShieldCheck,
  Smartphone,
} from 'lucide-react';
import PhoneRepairEstimator from '../components/PhoneRepairEstimator';
import { APP_CONFIG } from '../config/app';

const ReparationTelephone: React.FC = () => {
  const location = useLocation();
  const whatsappUrl = APP_CONFIG.contact.whatsappUrl;

  useEffect(() => {
    document.title = 'Réparation iPhone & Smartphone | SOS IT Services';
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute(
      'content',
      "Réparation d'iPhone rapide : écran cassé, batterie, connecteur de charge, vitre arrière. Devis immédiat, dépôt en atelier ou intervention à domicile autour de Genève."
    );
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://sos-it-services.fr/reparation-telephone');
    const ogTags: Record<string, string> = {
      'og:title': 'Réparation iPhone & Smartphone | SOS IT Services',
      'og:description': 'Écran, batterie, connecteur de charge — devis immédiat et réparation rapide de votre iPhone.',
      'og:url': 'https://sos-it-services.fr/reparation-telephone',
      'og:image': 'https://sos-it-services.fr/images/telephone-hero.png',
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    });
    window.scrollTo(0, 0);

    const hash = location.hash;
    if (hash) {
      setTimeout(() => {
        const element = document.getElementById(hash.substring(1));
        if (element) {
          const headerOffset = 80;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location]);

  const revealUp = {
    hidden: { opacity: 0, y: 24 },
    show: { opacity: 1, y: 0 },
  };

  const howItWorksSteps = [
    {
      id: '01',
      title: 'Sélectionnez votre appareil',
      description: 'Choisissez votre modèle d\'iPhone et le type de panne dans notre simulateur.',
      icon: ListChecks,
      delay: 0,
    },
    {
      id: '02',
      title: 'Recevez un prix immédiat',
      description: 'Le prix indicatif s\'affiche instantanément, confirmé après diagnostic.',
      icon: MessageSquare,
      delay: 0.08,
    },
    {
      id: '03',
      title: 'Dépôt ou domicile',
      description: 'Déposez l\'appareil en atelier ou prenez rendez-vous à domicile dans la région de Genève.',
      icon: Route,
      delay: 0.16,
    },
    {
      id: '04',
      title: 'Téléphone réparé',
      description: 'Réparation rapide avec pièces garanties, sous garantie 90 jours.',
      icon: ShieldCheck,
      delay: 0.24,
    },
  ] as const;

  const commonRepairs = [
    {
      title: 'Écran',
      description: "Remplacement d'écran cassé ou tactile défaillant, toutes générations d'iPhone.",
      badge: 'Le plus demandé',
      icon: Smartphone,
      accent: 'from-[#5865F2]/20 to-transparent',
    },
    {
      title: 'Batterie',
      description: "Batterie qui se décharge trop vite ou téléphone qui s'éteint seul : remplacement standard.",
      badge: 'Rapide',
      icon: BatteryCharging,
      accent: 'from-cyan-400/20 to-transparent',
    },
    {
      title: 'Connecteur de charge',
      description: 'Le téléphone ne charge plus ou difficilement : remplacement du connecteur de charge.',
      badge: 'Fiable',
      icon: Plug,
      accent: 'from-emerald-400/20 to-transparent',
    },
    {
      title: 'Vitre arrière',
      description: 'Vitre arrière fissurée ou brisée : remplacement esthétique et protection du châssis.',
      badge: 'Esthétique',
      icon: Layers,
      accent: 'from-amber-400/20 to-transparent',
    },
  ] as const;

  const faqItems = [
    {
      question: 'Combien de temps prend une réparation iPhone ?',
      answer:
        "La plupart des réparations (écran, batterie, connecteur de charge, vitre arrière) sont effectuées en moins d'une heure pour un dépôt en atelier. Pour une intervention à domicile, le délai dépend du créneau choisi lors de la prise de rendez-vous.",
    },
    {
      question: 'Les réparations sont-elles garanties ?',
      answer:
        'Oui, toutes nos réparations sont couvertes par une garantie de 90 jours sur les pièces et la main-d\'œuvre.',
    },
    {
      question: 'Utilisez-vous des pièces d\'origine ou compatibles ?',
      answer:
        "Nous utilisons des pièces de qualité compatible sélectionnées pour leur fiabilité, avec un prix optimisé par rapport aux pièces d'origine constructeur. Le détail vous est communiqué avant toute intervention.",
    },
    {
      question: 'Mon modèle d\'iPhone n\'est pas dans la liste, que faire ?',
      answer:
        "Contactez-nous directement par WhatsApp avec votre modèle et la panne concernée : nous vous ferons un devis personnalisé rapidement.",
    },
    {
      question: 'Dois-je déposer mon téléphone ou puis-je avoir un rendez-vous à domicile ?',
      answer:
        "Les deux sont possibles : dépôt en atelier pour un traitement rapide, ou intervention à domicile dans la région de Genève (Ain, Haute-Savoie, autour de Genève) selon disponibilité.",
    },
  ];

  const [openFaq, setOpenFaq] = useState<number | null>(0);

  return (
    <div className="pt-24 md:pt-32">
      {/* Hero */}
      <div className="relative py-20 min-h-[420px] flex items-center overflow-hidden bg-[#2F3136]">
        <div className="absolute inset-0 z-0">
          <img
            src="/images/telephone-hero.png"
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-[#2F3136]/70" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center"
            variants={revealUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.45 }}
            transition={{ duration: 0.55, ease: 'easeOut' }}
          >
            <motion.p
              className="inline-flex items-center gap-2 rounded-full border border-[#5865F2]/40 bg-[#5865F2]/15 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-[#B9C4FF] mb-4"
              animate={{ boxShadow: ['0 0 0 rgba(88,101,242,0)', '0 0 24px rgba(88,101,242,0.25)', '0 0 0 rgba(88,101,242,0)'] }}
              transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut' }}
            >
              Devis immédiat
            </motion.p>
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-6">
              Écran cassé, batterie faible ? <span className="text-[#5865F2]">On répare votre iPhone aujourd'hui</span>
            </h1>
            <p className="text-gray-200 text-base md:text-lg max-w-3xl mx-auto mb-8">
              Sélectionnez votre modèle et votre panne, recevez un prix immédiat. Dépôt en atelier ou intervention à domicile autour de Genève.
            </p>

            <motion.div
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
              variants={revealUp}
              transition={{ duration: 0.5, delay: 0.16, ease: 'easeOut' }}
            >
              <motion.a
                href="#devis"
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-[#5865F2] via-[#4752C4] to-[#5865F2] text-white font-medium rounded-lg hover:shadow-lg hover:shadow-[#5865F2]/30 transition-all duration-300"
                whileHover={{ scale: 1.03, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                Obtenir mon prix <ArrowRight className="ml-2 h-4 w-4" />
              </motion.a>
              <motion.a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 bg-white/5 border border-white/10 text-white font-medium rounded-lg hover:bg-white/10 transition-all duration-300"
                whileHover={{ scale: 1.03, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                Nous écrire sur WhatsApp
              </motion.a>
            </motion.div>

            <p className="mt-4 text-sm text-white/70">
              Vous préférez nous écrire ?{' '}
              <RouterLink to={APP_CONFIG.routes.contact} className="underline hover:text-white transition-colors">
                Formulaire de contact
              </RouterLink>
            </p>
          </motion.div>
        </div>
      </div>

      {/* Simulateur de devis */}
      <section id="devis" className="py-16 bg-[#36393F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.55, ease: 'easeOut' }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
              Quel est le prix de <span className="text-[#5865F2]">votre réparation ?</span>
            </h2>
            <p className="text-gray-400 text-sm">Sélectionnez votre modèle et votre panne, recevez un prix immédiat</p>
          </motion.div>
          <PhoneRepairEstimator whatsappUrl={whatsappUrl} />
        </div>
      </section>

      {/* Séparateur */}
      <div className="h-px bg-gradient-to-r from-transparent via-[#5865F2]/25 to-transparent" />

      {/* Comment ça marche */}
      <section className="relative py-20 bg-[#2F3136] overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.55, ease: 'easeOut' }}
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-[#5865F2]/35 bg-[#5865F2]/10 px-4 py-1.5 text-xs font-semibold tracking-wider text-[#9BA8FF] uppercase mb-4">
              Processus express
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
              Comment ça <span className="text-[#5865F2]">marche ?</span>
            </h2>
            <p className="text-gray-400 text-sm">Un parcours simple, clair et rapide en 4 étapes</p>
          </motion.div>

          <div className="relative max-w-5xl mx-auto">
            <div className="hidden md:block absolute top-11 left-[16%] right-[16%] h-px bg-gradient-to-r from-transparent via-[#5865F2]/35 to-transparent" />
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
              {howItWorksSteps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <div key={step.id} className="relative">
                    {index < howItWorksSteps.length - 1 && (
                      <div className="md:hidden absolute -bottom-3 left-1/2 -translate-x-1/2 w-px h-6 bg-gradient-to-b from-[#5865F2]/40 to-transparent z-10" />
                    )}
                    <motion.div
                      className="group relative text-center p-6 md:p-7 rounded-2xl border border-white/10 bg-gradient-to-b from-white/10 to-white/[0.03] backdrop-blur-sm"
                      initial={{ opacity: 0, y: 28 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, amount: 0.35 }}
                      transition={{ duration: 0.55, delay: step.delay, ease: 'easeOut' }}
                      whileHover={{ y: -6, scale: 1.01, transition: { duration: 0.22, ease: 'easeOut' } }}
                    >
                      <div className="relative z-10">
                        <motion.div
                          className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#5865F2]/20 border border-[#5865F2]/35 text-[#9BA8FF]"
                          whileHover={{ rotate: [0, -5, 5, 0] }}
                          transition={{ duration: 0.45 }}
                        >
                          <Icon className="h-6 w-6" />
                        </motion.div>
                        <span className="inline-flex rounded-full border border-[#5865F2]/30 bg-[#5865F2]/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#9BA8FF] mb-3">
                          Étape {step.id}
                        </span>
                        <h3 className="text-white font-semibold mb-2">{step.title}</h3>
                        <p className="text-gray-300/90 text-sm leading-relaxed">{step.description}</p>
                      </div>
                    </motion.div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Séparateur */}
      <div className="h-px bg-gradient-to-r from-transparent via-cyan-400/20 to-transparent" />

      {/* Réparations les plus courantes */}
      <section className="py-16 bg-[#36393F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-8"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.45 }}
            transition={{ duration: 0.45, ease: 'easeOut' }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-white">
              Réparations les plus <span className="text-[#5865F2]">courantes</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {commonRepairs.map((repair, index) => {
              const Icon = repair.icon;
              return (
                <motion.div
                  key={repair.title}
                  className="group relative overflow-hidden rounded-xl border border-white/12 bg-gradient-to-b from-white/10 to-white/[0.03] p-5"
                  initial={{ opacity: 0, y: 22 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.35 }}
                  transition={{ duration: 0.45, delay: index * 0.07, ease: 'easeOut' }}
                  whileHover={{ y: -6, scale: 1.01 }}
                >
                  <div className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${repair.accent} opacity-0 transition-opacity duration-300 group-hover:opacity-100`} />
                  <div className="relative">
                    <div className="mb-3 flex items-center justify-between">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-white/15 bg-white/10 text-[#B9C4FF]">
                        <Icon className="h-5 w-5" />
                      </div>
                      <span className="rounded-full border border-white/15 bg-white/5 px-2.5 py-1 text-[11px] uppercase tracking-wide text-gray-200">
                        {repair.badge}
                      </span>
                    </div>
                    <h3 className="text-white font-semibold mb-2">{repair.title}</h3>
                    <p className="text-sm text-gray-300 leading-relaxed">{repair.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ dédiée */}
      <section className="py-16 bg-[#2F3136]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            className="text-2xl md:text-3xl font-bold text-white text-center mb-8"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.45, ease: 'easeOut' }}
          >
            Questions <span className="text-[#5865F2]">fréquentes</span>
          </motion.h2>
          <div className="bg-[#36393F] rounded-lg overflow-hidden border border-gray-700 p-6">
            {faqItems.map((item, index) => {
              const isOpen = openFaq === index;
              return (
                <div key={item.question} className="border-b border-gray-700 last:border-0">
                  <button
                    className="w-full flex justify-between items-center py-4 text-left focus:outline-none"
                    onClick={() => setOpenFaq(isOpen ? null : index)}
                    aria-expanded={isOpen}
                  >
                    <h3 className="text-lg font-medium text-white">{item.question}</h3>
                    {isOpen ? (
                      <ChevronUp className="text-[#5865F2] h-5 w-5 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="text-[#5865F2] h-5 w-5 flex-shrink-0" />
                    )}
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.28, ease: 'easeInOut' }}
                        className="overflow-hidden"
                      >
                        <p className="text-gray-300 pb-4">{item.answer}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Zones d'intervention locales */}
      <section className="py-14 bg-[#36393F] border-t border-white/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            className="text-2xl md:text-3xl font-bold text-white text-center mb-3"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.45, ease: 'easeOut' }}
          >
            Intervention à domicile dans la région de <span className="text-[#5865F2]">Genève</span>
          </motion.h2>
          <p className="text-gray-400 text-center mb-8 max-w-xl mx-auto">
            En plus du dépôt en atelier, nous intervenons à domicile pour la réparation de votre téléphone dans les villes suivantes.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            {[
              { label: 'Genève', href: '/depannage-informatique-region-geneve' },
              { label: 'Saint-Julien-en-Genevois', href: '/depannage-informatique-saint-julien-en-genevois' },
              { label: 'Viry', href: '/depannage-informatique-viry' },
              { label: 'Annemasse', href: '/depannage-informatique-annemasse' },
              { label: 'Annecy', href: '/depannage-informatique-annecy' },
              { label: 'Gex', href: '/depannage-informatique-gex' },
            ].map(({ label, href }) => (
              <RouterLink
                key={href}
                to={href}
                className="flex items-center gap-2 bg-white/5 hover:bg-[#5865F2]/20 border border-white/10 hover:border-[#5865F2]/50 text-gray-300 hover:text-white px-4 py-2 rounded-full text-sm transition-colors"
              >
                <MapPin className="w-3.5 h-3.5 text-[#5865F2]" /> {label}
              </RouterLink>
            ))}
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section className="py-14 bg-[#2F3136] border-t border-white/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="rounded-2xl border border-[#5865F2]/35 bg-gradient-to-r from-[#5865F2]/20 via-[#5865F2]/10 to-cyan-400/10 p-7 md:p-8 text-center"
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            <p className="text-[#C7D0FF] text-xs uppercase tracking-wider mb-3">Prêt à réparer votre téléphone ?</p>
            <h3 className="text-xl md:text-2xl font-bold text-white mb-3">
              Décrivez votre panne, recevez un prix immédiat
            </h3>
            <p className="text-gray-200 text-sm md:text-base mb-6">
              Devis gratuit, dépôt en atelier ou intervention à domicile autour de Genève.
            </p>
            <a
              href="#devis"
              className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-[#5865F2] via-[#4752C4] to-[#5865F2] text-white font-medium rounded-lg hover:shadow-lg hover:shadow-[#5865F2]/30 transition-all duration-300"
            >
              Obtenir mon prix <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ReparationTelephone;
